import React, { useState, useCallback } from "react";
import { Text, Button } from "@jsq/ds/core";
import { useFormContext, FormRadioGroup, FormRadioButton } from "@jsq/ds/forms";
import { Colors } from "@jsq/ds/styles";
import { withClientContext } from "~/apollo/client";
import { useGetAllCurrenciesQuery } from "~/graphql/mainHooks";
import { useGetCountryNamesQuery, useGetStateBasicsQuery } from "~/graphql/supergraphHooks";
import { CustomFieldOption, PrefillOption } from "../../types";
import * as styles from "../styles_vanilla.css";

export type PrefillOptionsPanelProps = {
  show: boolean;
  onClose: () => void;
  fieldName: string;
  currentOptions: Omit<CustomFieldOption, "id">[];
};

export const PrefillOptionsPanel: React.FC<PrefillOptionsPanelProps> = ({
  show,
  onClose,
  fieldName,
  currentOptions,
}) => {
  const { values, setFieldValue } = useFormContext<{ prefillOption: PrefillOption }>();
  const [isPopulating, setIsPopulating] = useState(false);

  // Get prefill selection from form values
  const selectedPrefillOption = values.prefillOption || "country";

  // GraphQL query for countries
  const {
    data: countryData,
    loading: countryLoading,
    error: countryError,
  } = useGetCountryNamesQuery({
    skip: selectedPrefillOption !== "country" || !show,
    errorPolicy: "none",
  });

  // GraphQL query for states
  const {
    data: stateData,
    loading: stateLoading,
    error: stateError,
  } = useGetStateBasicsQuery({
    skip: selectedPrefillOption !== "state" || !show,
    errorPolicy: "none",
  });

  // GraphQL query for currencies (staff endpoint)
  const {
    data: currencyData,
    loading: currencyLoading,
    error: currencyError,
  } = useGetAllCurrenciesQuery({
    context: withClientContext("staff"),
    skip: selectedPrefillOption !== "currency" || !show,
    errorPolicy: "none",
  });

  // Helper function to remove duplicate options based on label
  const removeDuplicateOptions = useCallback(
    (
      existing: Omit<CustomFieldOption, "id">[],
      newOpts: Omit<CustomFieldOption, "id">[],
    ): Omit<CustomFieldOption, "id">[] => {
      const existingLabels = new Set(existing.map((opt) => opt.label.toLowerCase().trim()));
      const uniqueNewOptions = newOpts.filter(
        (opt) => !existingLabels.has(opt.label.toLowerCase().trim()),
      );
      return [...existing, ...uniqueNewOptions];
    },
    [],
  );

  const getFormattedOptionCode = (option: string) => {
    return option
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, "")
      .replace(/\s+/g, "_")
      .trim();
  };

  // Populate options based on selected prefill type
  const populateOptions = useCallback(() => {
    setIsPopulating(true);

    let newOptions: Omit<CustomFieldOption, "id">[] = [];

    switch (selectedPrefillOption) {
      case "country":
        if (countryData?.countries?.edges) {
          newOptions = countryData.countries.edges
            .map((edge) => edge.node)
            .filter((node): node is NonNullable<typeof node> => node !== null && !!node?.name)
            .map((node) => ({
              label: node.name,
              value: getFormattedOptionCode(node.name),
              isActive: true,
            }));
        }
        break;
      case "state":
        if (stateData?.states?.edges) {
          newOptions = stateData.states.edges
            .map((edge) => edge.node)
            .filter((node): node is NonNullable<typeof node> => node !== null && !!node?.name)
            .map((node) => ({
              label: node.name,
              value: getFormattedOptionCode(node.name),
              isActive: true,
            }));
        }
        break;
      case "currency":
        if (currencyData?.currencies) {
          newOptions = currencyData.currencies
            .filter(
              (currency): currency is NonNullable<typeof currency> =>
                currency !== null && !!currency?.fullName,
            )
            .map((currency) => ({
              label: `${currency.code} - ${currency.fullName}`,
              value: getFormattedOptionCode(`${currency.code}_${currency.fullName}`),
              isActive: true,
            }));
        }
        break;
    }

    // Remove duplicates and add the new options to existing ones
    const deduplicatedOptions = removeDuplicateOptions(currentOptions, newOptions);
    void setFieldValue(fieldName, deduplicatedOptions);
    onClose();

    setIsPopulating(false);
  }, [
    selectedPrefillOption,
    countryData,
    stateData,
    currencyData,
    currentOptions,
    setFieldValue,
    fieldName,
    onClose,
    removeDuplicateOptions,
  ]);

  const shouldDisablePopulateButton = () => {
    return (
      isPopulating ||
      (selectedPrefillOption === "country" && (countryLoading || !!countryError)) ||
      (selectedPrefillOption === "state" && (stateLoading || !!stateError)) ||
      (selectedPrefillOption === "currency" && (currencyLoading || !!currencyError))
    );
  };

  const getPopulateButtonText = () => {
    if (isPopulating) {
      return "Populating...";
    }
    if (countryLoading || stateLoading || currencyLoading) {
      return "Loading...";
    }
    return "Populate";
  };

  if (!show) {
    return null;
  }

  return (
    <div className={styles.prefillContainer}>
      <Text variant="regular-md" style={{ marginBottom: "12px" }}>
        Select a data source to prefill options:
      </Text>

      <FormRadioGroup name="prefillOption" className={styles.prefillRadioGroup}>
        <FormRadioButton value="country">Country</FormRadioButton>
        <FormRadioButton value="state">State</FormRadioButton>
        <FormRadioButton value="currency">Currency</FormRadioButton>
      </FormRadioGroup>

      {/* Display error message if query failed */}
      {(countryError || stateError || currencyError) && (
        <Text variant="regular-sm" style={{ marginBottom: "12px", color: Colors.Danger600 }}>
          Failed to load {selectedPrefillOption} data
        </Text>
      )}

      <div className={styles.prefillActions}>
        <Button variant="neutral" onClick={onClose} data-testid="cancel-prefill-button">
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={populateOptions}
          disabled={shouldDisablePopulateButton()}
          data-testid="populate-button"
        >
          {getPopulateButtonText()}
        </Button>
      </div>
    </div>
  );
};
